import React, { Component } from 'react'

import { RouteComponentProps } from 'react-router-dom'
import { WithTranslation } from 'react-i18next'
import { ChildProps, Query } from 'react-apollo'
import { InputProps } from './calculationMaterialQuery'
import { CALCULATION_PROJECT_QUERY } from './calculationProjectQuery'
import { PROJECT_QUERY } from './projectQuery'
import { AuthStore } from 'redux/reducers/auth'

import isNil from 'lodash/isNil'
import defaultTo from 'lodash/defaultTo'
import get from 'lodash/get'
import cloneDeep from 'lodash/cloneDeep'

import { getPrintTemplateLink, getProjectId } from 'utils'

import PopUpMenu from 'components/ui/popUpMenu'
import PageLayout from 'components/layout/pageLayout'
import SubNavigation from 'components/layout/subNavigation'
import Head from 'components/layout/head'
import Button from 'components/ui/button'
import Select, { IOption } from 'components/ui/select'
import materialRequirementConfig from './materialRequirementTable.config'
import materialRequirementResultConfig from './materialRequirementTableResult.config'
import Table from 'components/layout/table'
import TableResult from 'components/layout/tableResult'

interface CalculationMaterialRequirementProps
  extends ChildProps<InputProps, Response>,
    RouteComponentProps<any>,
    WithTranslation {
  auth: AuthStore
  positionData: any
}

type CalculationMaterialRequirementState = {}

class CalculationMaterialRequirement extends Component<
  CalculationMaterialRequirementProps,
  CalculationMaterialRequirementState
> {
  constructor(props: CalculationMaterialRequirementProps) {
    super(props)
    this.state = {}
  }

  handleCalculationChange = (option: IOption) => {
    const { history } = this.props
    const projectId = getProjectId(this.props)
    const calculationId = String(option.value)

    history.push(
      projectId
        ? `/projects/${projectId}/calculations/${calculationId}/material-requirement`
        : `/calculations/${calculationId}/material-requirement`,
    )
  }

  handlePrint = () => {
    const {
      auth: { apiToken },
    } = this.props
    const { calculationId } = this.props.match.params
    const projectId = getProjectId(this.props)
    const { projectNumber: calcProjectId } = this.props.calcProject

    const version = !isNil(projectId) ? 'v1' : calculationId
    const eleId = !isNil(projectId) ? Number(calculationId) : 1
    const projNr = !isNil(projectId) ? projectId : calcProjectId

    if (!isNil(projNr)) {
      window.open(
        getPrintTemplateLink('calculationMaterialRequisition', {
          version,
          projNr,
          eleId,
          token: defaultTo(apiToken, ''),
        }),
        '_blank',
      )
    }
  }

  transformCalculationsToOptions = (calculations: any) =>
    calculations.map((item: any) => ({
      value: item.id,
      label: item.systemDescription,
    }))

  transformMaterials = (materials: any) => {
    const amount = defaultTo(get(this.props, 'positionData.getPosition.amount'), 0)

    return cloneDeep(materials).map((material: any) => {
      Object.keys(material).map((key: string) => {
        if (!isNil(material[key]) && !material[key].hasOwnProperty('value')) {
          switch (key) {
            case 'amount':
              material[key] = { value: Number(material[key]) * amount, unit: material.unit }
              break
            case 'totalPrice':
              material[key] = Number(material[key]) * amount
              break
          }
        }
        return null
      })
      return material
    })
  }

  render() {
    const { calculationId } = this.props.match.params
    const projectId = getProjectId(this.props)
    const { t, history } = this.props

    const loading = get(this.props, 'positionData.loading', true)
    const amount = get(this.props, 'positionData.getPosition.amount', 0)
    const totalPrice = get(this.props, 'positionData.getPosition.materialCosts.totalPrice', 0)
    const materials = get(this.props, 'positionData.getPosition.materialCosts.materials', [])

    return (
      <PageLayout pageTitle="Kalkulation">
        <Head
          subheadline="Zurück zur Übersicht"
          linkTo="/calculations"
          actions={
            <>
              <Button
                secondary
                onClick={() => {
                  projectId
                    ? history.push(`/projects/${projectId}/new-calculation`)
                    : history.push('/calculations/new-calculation')
                }}
              >
                {t('newCalculation')}
              </Button>
            </>
          }
          headline={
            <Query
              query={!isNil(projectId) ? PROJECT_QUERY : CALCULATION_PROJECT_QUERY}
              variables={{ projectId }}
              fetchPolicy="network-only"
            >
              {({ loading, data }: any | null) => {
                const calculations = defaultTo(
                  get(
                    data,
                    `${!isNil(projectId) ? 'getProject' : 'getCalculationProject'}.positions`,
                  ),
                  [],
                )
                const calculationsOptions = this.transformCalculationsToOptions(calculations)

                return (
                  <Select
                    onChange={this.handleCalculationChange}
                    plain
                    options={calculationsOptions}
                    value={defaultTo(
                      calculationsOptions.find((option: IOption) => option.value === calculationId),
                      {
                        label: t('changeCalculation'),
                        value: '',
                      },
                    )}
                    maxWidth={250}
                    isLoading={loading}
                  />
                )
              }}
            </Query>
          }
        />
        <SubNavigation
          items={[
            {
              label: t('overview'),
              link: projectId
                ? `/projects/${projectId}/calculations/${calculationId}`
                : `/calculations/${calculationId}`,
            },
            {
              link: projectId
                ? `/projects/${projectId}/calculations/${calculationId}/material-requirement`
                : `/calculations/${calculationId}/material-requirement`,
              label: t('materialRequirement'),
            },
            {
              link: projectId
                ? `/projects/${projectId}/calculations/${calculationId}/time-requirement`
                : `/calculations/${calculationId}/time-requirement`,
              label: t('timeRequirement'),
            },
            // {
            //   link: projectId
            //     ? `/projects/${projectId}/calculations/${calculationId}/certificate`
            //     : `/calculations/${calculationId}/certificate`,
            //   label: t('certificate'),
            // },
          ]}
          actions={
            <>
              <PopUpMenu
                className="ml-30"
                options={[
                  {
                    label: 'Materialbedarf drucken',
                    function: () => this.handlePrint(),
                  },
                ]}
              />
            </>
          }
        />

        <div className="mb-65">
          <h1 className="mx-48 section-heading">Materialbedarf</h1>
          <Table
            config={materialRequirementConfig}
            isLoading={loading}
            data={this.transformMaterials(materials)}
          />
          <div className="mt-30">
            <TableResult
              config={materialRequirementResultConfig}
              data={[
                {
                  desc: 'Summe Materialbedarf Gesamt',
                  totalPriceNetto: totalPrice * amount,
                },
              ]}
            />
          </div>
        </div>
      </PageLayout>
    )
  }
}

export default CalculationMaterialRequirement
